import { useState } from 'react';
import usePageTitle from '../hooks/usePageTitle';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Pencil, Phone, Mail, MapPin, FileText } from 'lucide-react';
import { getCustomers, updateCustomer } from '../api/customers';
import Modal from '../components/Modal';
import Pagination from '../components/Pagination';
import FilterBar from '../components/FilterBar';
import { useAuth } from '../context/AuthContext';
import { canWrite } from '../utils/permissions';
import { useTranslation } from 'react-i18next';

const EMPTY_FORM = { name: '', email: '', defaultAddress: '', notes: '' };

function InfoRow({ icon: Icon, value }) {
  if (!value) return null;
  return (
    <span className="flex items-center gap-1 text-xs text-gray-400">
      <Icon size={11} />
      {value}
    </span>
  );
}

export default function Customers() {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(0);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [formError, setFormError] = useState('');

  usePageTitle('Customers');
  const { t } = useTranslation();
  const { user } = useAuth();
  const write = canWrite(user?.role);
  const [filters, setFilters] = useState({});
  const handleFilter = (key, val) => { setFilters((f) => ({ ...f, [key]: val || undefined })); setPage(0); };
  const clearFilters = () => { setFilters({}); setPage(0); };
  const { data, isLoading } = useQuery({
    queryKey: ['customers', page, filters],
    queryFn: () => getCustomers(page, 10, filters).then((r) => r.data.data),
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['customers'] });

  const saveMutation = useMutation({
    mutationFn: (payload) => updateCustomer(modal.data.id, payload),
    onSuccess: () => { invalidate(); closeModal(); },
    onError: (err) => setFormError(err.response?.data?.message || 'Something went wrong.'),
  });

  const openEdit = (customer) => {
    setForm({
      name: customer.name,
      email: customer.email || '',
      defaultAddress: customer.defaultAddress || '',
      notes: customer.notes || '',
    });
    setFormError('');
    setModal({ data: customer });
  };

  const closeModal = () => setModal(null);

  const handleSubmit = (e) => {
    e.preventDefault();
    setFormError('');
    saveMutation.mutate({
      name: form.name,
      email: form.email || null,
      defaultAddress: form.defaultAddress || null,
      notes: form.notes || null,
    });
  };

  const customers = data?.content ?? [];
  const totalPages = data?.totalPages ?? 0;

  return (
    <div className="p-6 max-w-4xl">
      <div className="mb-6">
        <h1 className="text-xl font-semibold text-gray-900">{t('customers.title')}</h1>
        <p className="text-sm text-gray-500 mt-0.5">
          {data?.totalElements ?? 0} {t('common.total')} · {t('customers.subtitle')}
        </p>
      </div>

      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        {isLoading ? (
          <div className="py-16 text-center text-sm text-gray-400">{t('common.loading')}</div>
        ) : customers.length === 0 ? (
          <div className="py-16 text-center text-sm text-gray-400">{t('customers.noCustomers')}</div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50 text-left">
                <th className="px-4 py-3 font-medium text-gray-500">{t('common.customer')}</th>
                <th className="px-4 py-3 font-medium text-gray-500">{t('common.phone')}</th>
                <th className="px-4 py-3 font-medium text-gray-500">{t('common.address')}</th>
                <th className="px-4 py-3 font-medium text-gray-500">{t('common.since')}</th>
                <th className="px-4 py-3 w-12" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {customers.map((c) => (
                <tr key={c.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-4 py-3">
                    <p className="font-medium text-gray-800">{c.name}</p>
                    {c.email && (
                      <p className="text-xs text-gray-400 mt-0.5 flex items-center gap-1">
                        <Mail size={11} /> {c.email}
                      </p>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <span className="flex items-center gap-1.5 text-gray-600">
                      <Phone size={12} className="text-gray-400" />
                      {c.phone}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-gray-500 max-w-[200px]">
                    {c.defaultAddress ? (
                      <p className="truncate text-xs">{c.defaultAddress}</p>
                    ) : (
                      <span className="text-gray-300">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-gray-400 text-xs whitespace-nowrap">
                    {new Date(c.createdAt).toLocaleDateString('en-GB', {
                      day: '2-digit', month: 'short', year: 'numeric',
                    })}
                  </td>
                  {write && (
                    <td className="px-4 py-3">
                      <button onClick={() => openEdit(c)} className="p-1.5 rounded-lg text-gray-400 hover:text-blue-600 hover:bg-blue-50 transition-colors"><Pencil size={14} /></button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <Pagination page={page} totalPages={totalPages} onPageChange={setPage} />

      {/* Edit Modal */}
      {modal && (
        <Modal title={t('customers.editCustomer')} onClose={closeModal}>
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Phone — read-only */}
            <div className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2.5">
              <Phone size={14} className="text-gray-400 shrink-0" />
              <div>
                <p className="text-xs text-gray-400">{t('customers.phoneNote')}</p>
                <p className="text-sm font-medium text-gray-700">{modal.data.phone}</p>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('common.name')} <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                required
                autoFocus
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <span className="flex items-center gap-1.5"><Mail size={13} /> {t('common.email')}</span>
              </label>
              <input
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Optional"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <span className="flex items-center gap-1.5"><MapPin size={13} /> {t('customers.defaultAddress')}</span>
              </label>
              <textarea
                value={form.defaultAddress}
                onChange={(e) => setForm({ ...form, defaultAddress: e.target.value })}
                rows={2}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                placeholder="Optional"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <span className="flex items-center gap-1.5"><FileText size={13} /> {t('common.notes')}</span>
              </label>
              <textarea
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                rows={2}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                placeholder="Optional"
              />
            </div>

            {formError && (
              <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                {formError}
              </p>
            )}

            <div className="flex justify-end gap-2 pt-1">
              <button type="button" onClick={closeModal} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors">{t('common.cancel')}</button>
              <button type="submit" disabled={saveMutation.isPending} className="px-4 py-2 text-sm font-medium bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white rounded-lg transition-colors">
                {saveMutation.isPending ? t('common.saving') : t('common.save')}
              </button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}
